import {CheckTerms} from "components/CheckTerms";
import {Exist} from "components/Exist";
import {HOCCheckUserName} from "components/HOCCheckUserName";
import {Preloader} from "components/Preloader";
import {globalError} from "modules/actions/errors";
import {getSessionUser, userLogin, userLoginWithAcceptTerms} from "modules/actions/user";
import {RequestState} from "modules/enums";
import {isLoggedIn, sessionState} from "modules/selectors/user";
import React, {Fragment, PropsWithChildren, useCallback, useLayoutEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import RallySSO from "modules/utils/SSO/rallySSO";
import {SSO} from "modules/utils/SSO";

interface IMlbOktaError {
	redirect: boolean;
	message: {
		errorCode: "login_required" | string;
		errorSummary: string;
		name: string;
	};
}

type SessionType = ReturnType<typeof userLogin>["payload"];

const useSession = () => {
	const dispatch = useDispatch();
	const [ssoState, setSSOState] = useState<SessionType | null>(null);

	useLayoutEffect(() => {
		const sso = new SSO();

		const onSuccess = () => {
			setSSOState({
				...sso.toJSON(),
				isOptIn: true,
			});
		};

		const onError = (e: IMlbOktaError) => {
			dispatch(globalError(e));
		};

		if (RallySSO.IS_NATIVE) {
			void sso.initRallySSO().then(onSuccess).catch(onError);
		} else {
			if (SSO.IS_BACKDOOR_MODE) {
				dispatch(getSessionUser());
				return;
			}
			void sso.initWebAuthSSO().then(onSuccess).catch(onError);
		}
	}, []);

	const isLogged = useSelector(isLoggedIn);

	/**
	 *  Used only for backdoor mode
	 */
	const session = useSelector(sessionState);

	const onLogin = (isOptIn: boolean, acceptTerms: boolean) => {
		if (ssoState) {
			if (acceptTerms) {
				dispatch(userLoginWithAcceptTerms({...ssoState, isOptIn: Boolean(isOptIn)}));
			} else {
				dispatch(userLogin({...ssoState, isOptIn: Boolean(isOptIn)}));
			}
		}
	};

	return {
		isAuthorized: isLogged || session === RequestState.Received,
		onLogin,
		ssoState,
	};
};

export const HOCSession: React.FC<PropsWithChildren> = ({children}) => {
	const {ssoState, onLogin, isAuthorized} = useSession();

	const onSubmitPayload = useCallback(
		(isOptIn: boolean, acceptTerms: boolean) => {
			console.log(isOptIn, acceptTerms);
			onLogin(isOptIn, acceptTerms);
		},
		[onLogin]
	);

	return (
		<Fragment>
			{isAuthorized ? (
				<Fragment>{children}</Fragment>
			) : (
				<Fragment>
					<Exist when={!!ssoState?.uid}>
						<HOCCheckUserName uid={ssoState?.uid ?? ""}>
							<CheckTerms uid={ssoState?.uid ?? ""} onSubmit={onSubmitPayload} />
						</HOCCheckUserName>
					</Exist>
					<Exist when={!ssoState?.uid}>
						<Preloader />
					</Exist>
				</Fragment>
			)}
		</Fragment>
	);
};
